import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {ITEM, TabKey} from "../../../../utils/common";
import {IPreprocessedAnalyticsPayload} from "../../../../types/chart";
import {MarkerItem} from "../../../../types/home";
import useAuthStore from "../../../../store/authStore";
import useHomeStore from "../../../../store/homeStore";
import {useSignalR} from "../../../../utils/hooks";
import Api from "../../../../api/Api";
import {EVENT_ANALYTICS_ACTIVATED, EVENT_VALIDATION_ACTIVATED, trackUpdate} from "../../../../utils/matomoEvents";
import {DEFAULT_ANALYTICS, DEFAULT_VALIDATION} from "../../../../types/analytics.d";

interface PopupState {
  [key: string]: {
    key: TabKey;
    visible: boolean;
  };
}

export const usePopupManagement = (activePoint: MarkerItem | null | undefined, onChangeActiveKey: (key: (string | string[]), tabKey?: TabKey) => void) => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setShowLogin = useHomeStore(state => state.setShowLogin);

  const [showPopup, setShowPopup] = useState<PopupState>({});
  const [datasetHasNoAnalyticsVisible, setDatasetHasNoAnalyticsVisible] = useState<boolean>(false);
  const [datasetIsOfflineWarningVisible, setDatasetIsOfflineWarningVisible] = useState<boolean>(false);
  const [datasetOfflineText, setDatasetOfflineText] = useState<string>('');
  const [datasetNoReportVisible, setDatasetNoReportVisible] = useState<boolean>(false);
  const [isReportsVisible, setIsReportsVisible] = useState<boolean>(false);
  const [activePlotValidation, setActivePlotValidation] = useState<string[]>(['Altimeter Comparison']);
  const [updating, setUpdating] = useState<boolean>(false);
  const [links, setLinks] = useState<any[]>([]);
  const [error, setError] = useState<string>('');

  const { messageReceived, receivedMessageText, resetMessageState } = useSignalR("AnalyticsReceiveMessage", "JobResult");

  const onUpdatePreprocessedDataLink = useCallback(async (payload: IPreprocessedAnalyticsPayload): Promise<void> => {
    try {
      setUpdating(true);
      const result = await Api.chart.getPreprocessedAnalytics(payload);
      if (result) {
        if (result.Result) {
          setError('');
          setLinks(result.Result);
        } else {
          setLinks([]);
          setError(
            result.Error && result.Error.ErrorMessage
              ? result.Error.ErrorMessage
              : 'Failed to return data'
          );
        }
      }
    } catch {
      setError('Failed to return data');
    }
  }, []);

  useEffect(() => {
    if (messageReceived) {
      const hubMessageParts = receivedMessageText.split(':');
      let result: string;

      if (hubMessageParts.length > 1) {
        result = hubMessageParts[hubMessageParts.length - 1];
      } else {
        result = receivedMessageText;
      }

      if (result && result.toLowerCase() !== 'success') {
        setError(result);
      }

      setUpdating(false);
      resetMessageState();
    }
  }, [messageReceived, receivedMessageText, resetMessageState]);

  const onChangePoint = useCallback((id: string | string[], tabKey: TabKey) => {
    setShowPopup((prevState) => {
      const currentPopup = prevState?.[id as string];

      return {
        ...prevState,
        [id as string]: {
          ...currentPopup,
          key: tabKey
        }
      }
    })
    onChangeActiveKey(id)
  }, [activePoint])

  const handleVisibleNewPopup = useCallback((value: boolean): void => {
    if (activePoint) {
      setShowPopup((prevState) => ({
        ...prevState,
        [activePoint.id]: {
          ...(prevState?.[activePoint.id] || { key: ITEM.VALIDATION }),
          visible: value
        }
      }));
    }
  }, [activePoint]);

  const switchVisiblePopup = useCallback((id: string): void => {
    setShowPopup((prevState) => {
      const newState = { ...prevState };

      Object.keys(newState).forEach(pointId => {
        newState[pointId] = {
          ...newState[pointId],
          visible: false
        };
      });

      newState[id] = {
        ...(newState[id] || { key: ITEM.VALIDATION }),
        visible: true
      };

      return newState;
    });
  }, []);

  const handlePopupKey = useCallback((newKey: TabKey): void => {
    if (activePoint) {
      setShowPopup((prevState) => ({
        ...prevState,
        [activePoint.id]: {
          ...(prevState?.[activePoint.id] || { visible: true }),
          key: newKey
        }
      }));
    }
  }, [activePoint]);

  const onShowReports = useCallback((isDatasetHasReport: boolean, isDatasetOffline: boolean): void => {
    if (!isAuthenticated()) {
      message.warn('You are not authorized to see reports');
      setShowLogin(true);
      return;
    }

    if (!isDatasetHasReport) {
      setDatasetNoReportVisible(true);
      return;
    }

    if (!isDatasetOffline && isDatasetHasReport) {
      setIsReportsVisible(true);
    } else {
      setDatasetIsOfflineWarningVisible(true);
    }
  }, [isAuthenticated, setShowLogin]);

  const showNewPopup = useCallback((key: TabKey, isDatasetOffline: boolean): void => {
    if (key === 'validation' && !isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    if (key=== 'analytics' && activePoint?.dataset.includes('CMEMS')) {
      setDatasetHasNoAnalyticsVisible(true);
      return;
    }

    if (!isDatasetOffline && activePoint) {
      if (activePoint) {
        setShowPopup((prevState) => ({
          ...prevState,
          [activePoint.id]: {
            visible: true,
            key: key
          }
        }));
      }

      const coordinates = activePoint?.coordinates || [];
      const datasetId = activePoint?.dataset;

      if (coordinates.length && datasetId && key === 'analytics') {
        trackUpdate(EVENT_ANALYTICS_ACTIVATED, coordinates, datasetId, DEFAULT_ANALYTICS);
      } else if (coordinates.length && datasetId && key === 'validation') {
        trackUpdate(EVENT_VALIDATION_ACTIVATED, coordinates, datasetId, DEFAULT_VALIDATION);
      }
    } else {
      setDatasetIsOfflineWarningVisible(true);
    }
  }, [activePoint, isAuthenticated, setShowLogin]);

  return {
    showPopup,
    onChangePoint,
    datasetIsOfflineWarningVisible,
    datasetOfflineText,
    datasetNoReportVisible,
    isReportsVisible,
    activePlotValidation,
    updating,
    links,
    error,
    setDatasetOfflineText,
    datasetHasNoAnalyticsVisible,
    setDatasetHasNoAnalyticsVisible,
    setDatasetIsOfflineWarningVisible,
    setDatasetNoReportVisible,
    setIsReportsVisible,
    handleVisibleNewPopup,
    switchVisiblePopup,
    handlePopupKey,
    onShowReports,
    showNewPopup,
    onUpdatePreprocessedDataLink,
    setActivePlotValidation
  };
};
